import React, { useState, useContext, useEffect } from "react";
import { Tabs, Collapse, Spin, List, Typography, Button, Empty } from "antd";
import { ConopsContext } from "../../conops-hooks/ConopsContext";
import { from, of, Subject } from "rxjs";
import { debounceTime, switchMap, catchError } from "rxjs/operators";
import apiUrlconops from "../../../ApiEndpoint/configconops";
import apiClientconops from "../../../ApiEndpoint/apiClientconops";

import { useAuthContext } from "../../../utils/AuthContext";

const { TabPane } = Tabs;
const { Panel } = Collapse;

const ConopsCountyDetails = () => {
    const { countyObject, selectedState } = useContext(ConopsContext);
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(false);
    const fetchTrigger$ = new Subject();
    const [overview, setOverview] = useState("");
    const [overviewLoading, setOverviewLoading] = useState(false);
    const { user } = useAuthContext();
    const [countyDocuments, setCountyDocuments] = useState(null);
    const [documentsLoading, setDocumentsLoading] = useState(false);
    const [newsData, setNewsData] = useState([]);
    const [newsLoading, setNewsLoading] = useState(false);
    //contacts api integration
    useEffect(() => {
        if (!countyObject?.county || (!countyObject?.state && !selectedState)) {
            setContacts([]);
            return;
        }

        const subscription = fetchTrigger$
            .pipe(
                debounceTime(200),
                switchMap(() => {
                    setLoading(true); // start loader
                    const params = {
                        county: countyObject.county,
                        state: countyObject?.state ? countyObject.state : selectedState
                    };
                    return from(apiClientconops.get(`${apiUrlconops}/county-contacts`, { params })).pipe(
                        switchMap((res) => of(res.data?.data || [])),
                        catchError(() => of([]))
                    );
                })
            )
            .subscribe((data) => {
                setContacts(data);
                setLoading(false); // stop loader
            });

        // Trigger the initial API call
        fetchTrigger$.next();

        return () => subscription.unsubscribe();
    }, [countyObject?.county, countyObject?.state, selectedState]);


    //county overview api integration
    useEffect(() => {
        if (!countyObject?.county || (!countyObject?.state && !selectedState)) {
            setOverview("");
            return;
        }

        const subscription = fetchTrigger$
            .pipe(
                debounceTime(200),
                switchMap(() => {
                    setOverviewLoading(true); // start loader
                    const params = {
                        county: countyObject.county,
                        state: countyObject?.state ? countyObject.state : selectedState,
                        user_email: user.email
                    };

                    return from(
                        apiClientconops.get(`${apiUrlconops}/county_overview`, { params })
                    ).pipe(
                        switchMap((res) => of(res?.data || "")),
                        catchError(() => of("No overview available."))
                    );
                })
            )
            .subscribe((data) => {
                console.log("Overview API Data:", data);
                setOverview(data);
                setOverviewLoading(false); // stop loader
            });

        // Trigger the initial API call
        fetchTrigger$.next();

        return () => subscription.unsubscribe();
    }, [countyObject?.county, countyObject?.state, selectedState]);


    //finance and mom api integration
    useEffect(() => {
        if (!countyObject?.county || (!countyObject?.state && !selectedState)) {
            setCountyDocuments(null);
            return;
        }
        const subscription = fetchTrigger$
            .pipe(
                debounceTime(200),
                switchMap(() => {
                    setDocumentsLoading(true);

                    const params = {
                        county: countyObject.county,
                        state: countyObject?.state ? countyObject.state : selectedState
                    };

                    return from(
                        apiClientconops.get(`${apiUrlconops}/county-documents`, { params })
                    ).pipe(
                        switchMap((res) => {
                            const data = res.data;
                            if (!data) return of({});

                            const docTypes = ["financial", "mom"];

                            // ---- ✔ Build key-value object here ----
                            const result = {};

                            docTypes.forEach((type) => {
                                if (data[type]) {
                                    const doc = data[type];

                                    result[type] = {
                                        state: data.state,
                                        county: data.county,

                                        dateOfIssueDoc: doc.Date_of_Issue_of_Doc,
                                        documentId: doc.Unique_Document_ID,
                                        municipality: doc.Municipality_Name,
                                        documentType: doc.Document_Type,
                                        documentName: doc.Document_Name,
                                        documentSummary: doc.Document_Summary,
                                        policiesOrBudget: doc.Policies_or_Budget,
                                        highlightedIssues: doc.Highlighted_Issues,
                                        parsedDate: doc.parsed_date,
                                    };
                                }
                            });

                            return of(result); // 👈 returning key–value object
                        }),
                        catchError((err) => {
                            console.error("Error fetching county documents:", err);
                            return of({});
                        })
                    );
                })
            )
            .subscribe((docsObject) => {
                setCountyDocuments(docsObject); // 👈 { financial:{}, mom:{} }
                setDocumentsLoading(false);
            });

        fetchTrigger$.next();
        return () => subscription.unsubscribe();
    }, [countyObject?.county, countyObject?.state, selectedState]);


    // county news api integration
    useEffect(() => {
        if (!countyObject?.county || (!countyObject?.state && !selectedState)) {
            setNewsData([]);
            return;
        }

        const subscription = fetchTrigger$
            .pipe(
                debounceTime(200),
                switchMap(() => {
                    setNewsLoading(true); // start loader

                    const params = {
                        county: countyObject.county,
                        state: countyObject?.state ? countyObject.state : selectedState,
                        user_email: user.email
                    };

                    return from(
                        apiClientconops.get(`${apiUrlconops}/county_news`, { params })
                    ).pipe(
                        switchMap((res) => of(res?.data || [])),
                        catchError((err) => {
                            console.error("News API Error:", err);
                            return of([]); // fallback
                        })
                    );
                })
            )
            .subscribe((data) => {
                console.log("News API Data:", data);
                setNewsData(data);
                setNewsLoading(false);
            });

        // trigger initial request
        fetchTrigger$.next();

        return () => subscription.unsubscribe();
    }, [countyObject?.county, countyObject?.state, selectedState]);

    return (
        <>
            <div className="mt-2">
                <h5 className="facility-title">{countyObject?.county}</h5>
                <Tabs defaultActiveKey="1" type="card">
                    <TabPane tab="Overview" key="1">
                        <Overview overview={overview} overviewLoading={overviewLoading} />
                    </TabPane>

                    <TabPane tab="Contacts" key="2">
                        <Contact contacts={contacts} loading={loading} />
                    </TabPane>
                    <TabPane tab="Financials" key="3">
                        <Finanicals countyDocuments={countyDocuments?.financial} documentsLoading={documentsLoading} />
                    </TabPane>
                    <TabPane tab="MOMs" key="4">
                        <MOMs countyDocuments={countyDocuments?.mom} documentsLoading={documentsLoading} />
                    </TabPane>
                    <TabPane tab="News" key="5">
                        <News newsData={newsData} newsLoading={newsLoading} />
                    </TabPane>
                </Tabs>
            </div>
        </>

    );
};



const Overview = ({ overview, overviewLoading }) => {

    return (
        <>
            <div className="container p-0" style={{ maxHeight: "66vh", overflowY: "scroll" }}>
                {overviewLoading ? (
                    <div className="text-center">
                        <Spin />
                    </div>
                ) : overview ? (
                    <>
                        {/* County Overview */}
                        {/* <h5 className="facility-title">County Overview</h5> */}
                        <p style={{ fontSize: "14px" }}>{overview?.countyOverview}</p>

                        {/* County Summary */}
                        {overview?.countySummary && (
                            <>
                                <h6 className="mt-3">County Summary</h6>
                                <ul style={{ fontSize: "14px" }}>
                                    {Object.entries(overview.countySummary).map(
                                        ([key, value]) => {
                                            const formattedKey = key
                                                .replace(/([A-Z])/g, " $1")
                                                .replace(/^./, (str) => str.toUpperCase()); // Capitalize first letter

                                            // Special case: website → clickable link
                                            if (key === "countyWebsite") {
                                                return (
                                                    <li key={key}>
                                                        <strong>{formattedKey}:</strong>{" "}
                                                        <a
                                                            href={value}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {value}
                                                        </a>
                                                    </li>
                                                );
                                            }

                                            return (
                                                <li key={key}>
                                                    <strong>{formattedKey}:</strong> {value}
                                                </li>
                                            );
                                        }
                                    )}
                                </ul>
                            </>
                        )}

                        {/* Current Initiatives */}
                        {overview?.currentInitiatives?.length > 0 && (
                            <>
                                <h6 className="mt-3">Current Initiatives</h6>
                                <ul style={{ fontSize: "14px" }}>
                                    {overview.currentInitiatives.map((item, idx) => (
                                        <li key={idx}>{item}</li>
                                    ))}
                                </ul>
                            </>
                        )}

                        {/* Key Roles */}
                        {overview?.keyRoles?.length > 0 && (
                            <>
                                <h6 className="mt-3">Key Roles</h6>
                                <ul style={{ fontSize: "14px" }}>
                                    {overview.keyRoles.map((role, idx) => (
                                        <li key={idx}>{role}</li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </>
                ) : (
                    <p style={{ textAlign: "center" }}>No overview available.</p>
                )}
            </div>
        </>
    );
}


const Contact = ({ contacts, loading }) => {

    return (
        <div className="opportunity-container" style={{ maxHeight: "66vh", overflowY: "scroll" }}>
            {loading ? (
                <div>
                    <Spin />
                </div>
            ) : contacts.length > 0 ? (
                contacts.map((contact, idx) => (
                    <div key={idx} className="opportunity">
                        <div className="opportunity-name">
                            {contact.Person_Name || "N/A"}
                        </div>
                        <ul className="opportunity-details" style={{ listStyleType: "disc", paddingLeft: "20px" }}>
                            <li><strong>Title:</strong> {contact.Designation || "N/A"}</li>
                            <li><strong>Email:</strong> <span className="text-primary">{contact.Email || "N/A"}</span></li>
                            <li><strong>Phone:</strong> {contact.Phone_Number || "N/A"}</li>
                        </ul>
                    </div>
                ))
            ) : (
                <div style={{ textAlign: "center" }}>
                    No contacts found for this county.
                </div>
            )}
        </div>
    )
}

const Finanicals = ({ countyDocuments, documentsLoading }) => {
    return (
        <div className="opportunity-container" style={{ maxHeight: "66vh", overflowY: "scroll" }}>
            {
                documentsLoading ? (
                    <Spin />
                ) : countyDocuments ? (
                    <div className="county-doc-card">
                        <h6 style={{ float: 'right' }}>Date:{countyDocuments.dateOfIssueDoc}</h6>
                        <h5 className="facility-title pt-4">Document Summary</h5>
                        <p>{countyDocuments.documentSummary}</p>
                        <span> <strong>Document Link:</strong>{" "}</span>
                        <a
                            href={countyDocuments.documentName}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                wordBreak: "break-all",
                                color: "#1677ff",
                            }}
                        >
                            {countyDocuments.documentName}
                        </a>
                        <h5 className="facility-title pt-4">Policies / Budget</h5>
                        <p>{countyDocuments.policiesOrBudget}</p>
                        <h5 className="facility-title">Highlighted Issues</h5>
                        <p>{countyDocuments.highlightedIssues}</p>
                    </div>
                ) : (
                    <p style={{ textAlign: "center" }}>No documents available for this county.</p>
                )
            }

        </div >
    )
}

const MOMs = ({ countyDocuments, documentsLoading }) => {
    return (
        <div className="opportunity-container" style={{ maxHeight: "66vh", overflowY: "scroll" }}>
            {
                documentsLoading ? (
                    <Spin />
                ) : countyDocuments ? (
                    <div className="county-doc-card">
                        <h6 style={{ float: 'right' }}>Date:{countyDocuments.dateOfIssueDoc}</h6>
                        <h5 className="facility-title pt-4">Document Summary</h5>
                        <p>{countyDocuments.documentSummary}</p>
                        <span> <strong>Document Link:</strong>{" "}</span>
                        <a
                            href={countyDocuments.documentName}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                wordBreak: "break-all",
                                color: "#1677ff",
                            }}
                        >
                            {countyDocuments.documentName}
                        </a>
                        <h5 className="facility-title pt-4">Policies / Budget</h5>
                        <p>{countyDocuments.policiesOrBudget}</p>
                        <h5 className="facility-title">Highlighted Issues</h5>
                        <p>{countyDocuments.highlightedIssues}</p>
                    </div>
                ) : (
                    <p style={{ textAlign: "center" }}>No documents available for this county.</p>
                )
            }

        </div >
    )

}


const News = ({ newsData, newsLoading }) => {
    return (
        <div style={{ maxHeight: "66vh", overflowY: "auto" }}>
            {newsLoading ? (
                <Spin />
            ) : newsData.length > 0 ? (
                <List
                    dataSource={newsData}
                    renderItem={(item, index) => (
                        <List.Item
                            key={index}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                padding: "12px 0",
                                borderBottom: "1px solid #f0f0f0",
                            }}
                        >
                            <Typography.Text strong style={{ fontSize: 16 }}>
                                {item.Title}
                            </Typography.Text>
                            <Typography.Text type="secondary" style={{ marginBottom: 8 }}>
                                {new Date(item.date).toLocaleDateString()}
                            </Typography.Text>
                            <Button
                                type="primary"
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                            >
                                Read More
                            </Button>
                        </List.Item>
                    )}
                />
            ) : (
                <Empty description="No data found" />
            )}
        </div>
    )
}
export default ConopsCountyDetails;